import type { Description } from "@/components/shared/Typography";
import { EMAIL, PRETTY_PHONE_NUMBER } from "@/lib/consts";

export type LegalSection = {
  heading: string;
  body: Description;
};

export type LegalDocument = {
  effectiveNote: string;
  intro: Description;
  sections: LegalSection[];
};

// NOTE: This is a general-purpose privacy policy template. It should be
// reviewed by the firm's attorney before being relied upon.
const PRIVACY_POLICY: LegalDocument = {
  effectiveNote:
    "This Privacy Policy is a general template and should be reviewed by legal counsel before publication.",
  intro: [
    "Orsak Langner & Barthel PLLC (“Orsak CPAs,” “we,” “us,” or “our”) respects your privacy and is committed to protecting the personal and financial information you share with us. This Privacy Policy explains what information we collect, how we use it, and the choices you have.",
    "By using our website or engaging our services, you consent to the practices described in this policy.",
  ],
  sections: [
    {
      heading: "Information We Collect",
      body: [
        "We collect information you provide directly to us—such as your name, email address, phone number, and any details you submit through our contact forms or during an engagement.",
        "When you visit our website, we may also automatically collect limited technical information such as your browser type, device, and pages visited, through analytics tools.",
      ],
    },
    {
      heading: "How We Use Your Information",
      body: [
        "We use the information we collect to respond to your inquiries, provide accounting, tax, audit, and advisory services, communicate with you about your engagement, and improve our website and services.",
        "We do not sell your personal information.",
      ],
    },
    {
      heading: "How We Share Your Information",
      body: "We share information only as needed to provide our services—for example, with regulatory authorities when required by law, or with trusted service providers who assist our operations under confidentiality obligations. We may also disclose information when required by law or to protect our legal rights.",
    },
    {
      heading: "Data Security",
      body: "We maintain reasonable administrative, technical, and physical safeguards designed to protect your information. However, no method of transmission or storage is completely secure, and we cannot guarantee absolute security.",
    },
    {
      heading: "Cookies & Analytics",
      body: "Our website may use cookies and similar technologies to understand how visitors use the site and to improve your experience. You can adjust your browser settings to refuse cookies, though some features may not function properly as a result.",
    },
    {
      heading: "Your Choices",
      body: "You may request that we update or delete the personal information we hold about you, or opt out of non-essential communications, by contacting us using the details below.",
    },
    {
      heading: "Changes to This Policy",
      body: "We may update this Privacy Policy from time to time. Any changes will be posted on this page.",
    },
    {
      heading: "Contact Us",
      body: `If you have questions about this Privacy Policy, contact Orsak Langner & Barthel PLLC at ${EMAIL} or ${PRETTY_PHONE_NUMBER}.`,
    },
  ],
};

export function getPrivacyPolicy(): LegalDocument {
  return PRIVACY_POLICY;
}
